import React, { useCallback, useContext, useEffect, useState } from 'react';
import { LuTrash2 } from 'react-icons/lu';
import AuthContext from '../shared/AuthContext';
import { adminService } from '../shared/hooks/AuthService';
import { formatLastSeen } from '../../utils/formatLastSeen';
import './AdminUsersPage.css';

const REFRESH_MS = 30_000;

function rowUserId(row) {
  const raw = row?.userId ?? row?.id;
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function apiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  return data.message || data.detail || data.description || data.error || fallback;
}

export default function AdminUsersPage() {
  const { apiClient, user } = useContext(AuthContext);
  const profile = user || JSON.parse(localStorage.getItem('userProfile') || 'null');
  const currentUserId = rowUserId(profile);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [roleSavingId, setRoleSavingId] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  const [testConfig, setTestConfig] = useState(null);
  const [testProvisioning, setTestProvisioning] = useState(false);
  const [testCredentials, setTestCredentials] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/get-users');
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Admin access required.' : 'Could not load users.');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    loadUsers();
    const id = window.setInterval(loadUsers, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [loadUsers]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminService.getTestUserConfig();
        if (!cancelled) setTestConfig(res?.data ?? null);
      } catch {
        if (!cancelled) setTestConfig({ enabled: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleProvisionTestUser = async () => {
    setTestProvisioning(true);
    setError(null);
    setTestCredentials(null);
    try {
      const res = await adminService.provisionTestUser(true);
      setTestCredentials(res?.data ?? null);
      setActionMessage('Test user ready for QA sign-in.');
      await loadUsers();
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not create test user. Enable qnaread.testing.enabled on the API.'));
    } finally {
      setTestProvisioning(false);
    }
  };

  const onlineCount = rows.filter((r) => r.online).length;

  const deleteUserOnServer = async (userId) => {
    try {
      await apiClient.delete(`/api/admin/users/${userId}`);
    } catch (deleteErr) {
      if (deleteErr.response?.status === 404 || deleteErr.response?.status === 405) {
        await apiClient.post('/api/admin/delete-user', { userId });
        return;
      }
      throw deleteErr;
    }
  };

  const handleDeleteUser = async (row) => {
    const userId = rowUserId(row);
    if (userId == null) {
      setError('Missing user id for this row. Restart the API and refresh the page.');
      return;
    }

    const label = row.email || row.name || `user #${userId}`;
    const confirmed = window.confirm(
      `Permanently delete ${label}?\n\nThis removes their account and all learning data (history, saves, reviews, XP, tokens). This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(userId);
    setActionMessage('');
    setError(null);
    try {
      await deleteUserOnServer(userId);
      setRows((prev) => prev.filter((r) => rowUserId(r) !== userId));
      setActionMessage(`Deleted ${label}.`);
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not delete user.'));
    } finally {
      setDeletingId(null);
    }
  };

  const isSelf = (userId) =>
    currentUserId != null && userId != null && currentUserId === userId;

  const setPlatformAdmin = async (userId, grantAdmin) => {
    setActionMessage('');
    setError(null);
    setRoleSavingId(userId);
    try {
      await apiClient.patch(`/api/admin/users/${userId}/platform-role`, { admin: grantAdmin });
      setRows((prev) => prev.map((r) => {
        if (rowUserId(r) !== userId) return r;
        const roles = grantAdmin ? ['ROLE_USER', 'ROLE_ADMIN'] : ['ROLE_USER'];
        return { ...r, roles, platformAdmin: grantAdmin };
      }));
      setActionMessage(grantAdmin ? 'User is now a platform admin.' : 'Platform admin role removed.');
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not update admin role.'));
    } finally {
      setRoleSavingId(null);
    }
  };

  return (
      <div className="admin-users-page">
        <header className="admin-users-header">
          <div>
            <h1>Users</h1>
            <p className="admin-users-sub">
              Online = active in the last 5 minutes. Refreshes every 30 seconds.
              Delete removes the account and all related records.
            </p>
          </div>
          <div className="admin-users-stats" aria-live="polite">
            <span className="admin-users-stat">
              <strong>{onlineCount}</strong> online
            </span>
            <span className="admin-users-stat">
              <strong>{rows.length}</strong> total
            </span>
          </div>
        </header>

        {testConfig?.enabled && (
          <section className="admin-users-test-panel" aria-labelledby="admin-test-user-heading">
            <div>
              <h2 id="admin-test-user-heading">QA test account</h2>
              <p className="admin-users-sub">
                Pro billing + all paid entitlements when unlock is on (testing only). Not a platform admin —
                delete anytime from the table below.
              </p>
            </div>
            <button
              type="button"
              className="admin-users-test-btn"
              disabled={testProvisioning}
              onClick={handleProvisionTestUser}
            >
              {testProvisioning ? 'Working…' : 'Create / reset test user'}
            </button>
            {testCredentials && (
              <div className="admin-users-test-creds" role="status">
                <p>
                  <strong>Email:</strong> {testCredentials.email}
                </p>
                <p>
                  <strong>Password:</strong> {testCredentials.password}
                </p>
                <p className="admin-users-muted-inline">
                  Effective plan: {testCredentials.effectivePlan}
                  {testCredentials.unlockPaidFeatures ? ' (paid features unlocked)' : ''}
                </p>
              </div>
            )}
          </section>
        )}

        {error && (
          <div className="admin-users-error" role="alert">
            {error}
          </div>
        )}

        {actionMessage && !error && (
          <p className="admin-users-success" role="status">
            {actionMessage}
          </p>
        )}

        {loading && rows.length === 0 ? (
          <p className="admin-users-muted">Loading users…</p>
        ) : (
          <div className="admin-users-table-wrap">
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th scope="col">Status</th>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Plan</th>
                  <th scope="col">Last seen</th>
                  <th scope="col">Platform admin</th>
                  <th scope="col">
                    <span className="admin-users-sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const userId = rowUserId(row);
                  return (
                  <tr key={userId ?? row.email}>
                    <td>
                      <span
                        className={`admin-users-pill ${row.online ? 'admin-users-pill--online' : ''}`}
                      >
                        <span className="admin-users-dot" aria-hidden />
                        {row.online ? 'Online' : 'Offline'}
                      </span>
                    </td>
                    <td>
                      {row.name || '—'}
                      {row.testAccount ? (
                        <span className="admin-users-test-badge">Test</span>
                      ) : null}
                    </td>
                    <td>{row.email}</td>
                    <td>{row.subscriptionPlan || 'FREE'}</td>
                    <td>{formatLastSeen(row.lastSeenAt)}</td>
                    <td>
                      <label className="admin-users-admin-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(row.platformAdmin || row.roles?.includes('ROLE_ADMIN'))}
                        disabled={
                          userId == null
                          || roleSavingId === userId
                          || row.testAccount
                        }
                        onChange={(e) => setPlatformAdmin(userId, e.target.checked)}
                        />
                        {row.platformAdmin || row.roles?.includes('ROLE_ADMIN') ? 'Admin' : 'User'}
                      </label>
                    </td>
                    <td className="admin-users-actions-cell">
                      <button
                        type="button"
                        className="admin-users-delete-btn"
                        disabled={userId == null || deletingId === userId || isSelf(userId)}
                        title={
                          userId == null
                            ? 'User id unavailable'
                            : isSelf(userId)
                              ? 'You cannot delete your own account here'
                              : 'Delete account permanently'
                        }
                        aria-label={`Delete account ${row.email || row.name || userId}`}
                        onClick={() => handleDeleteUser(row)}
                      >
                        {deletingId === userId ? (
                          <span className="admin-users-delete-label">…</span>
                        ) : (
                          <LuTrash2 aria-hidden className="admin-users-delete-icon" />
                        )}
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}
